import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EMSCalculatorForm } from "@/components/ems/EMSCalculatorForm";
import { EmailGate } from "@/components/ems/EmailGate";

const EMSCalculator = () => {
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const [hasAccess, setHasAccess] = useState(() => {
    // Initialize from localStorage synchronously
    return localStorage.getItem('ems_calculator_email_verified') === 'true';
  });

  // Force Hungarian language on this page - calculator is only available in Hungarian
  useEffect(() => {
    if (language !== 'hu') {
      setLanguage('hu');
    }
  }, [language, setLanguage]);

  const handleAccessGranted = useCallback(() => {
    setHasAccess(true);
  }, []);

  useEffect(() => {
    // Only run when user has access
    if (!hasAccess) return;

    window.scrollTo(0, 0);
    
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      const element = document.getElementById(sectionId);
      
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
    
    // Small delay to ensure DOM is ready after access is granted
    const timeoutId = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("revealed");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );

      document.querySelectorAll(".reveal-on-scroll").forEach((el) => {
        observer.observe(el);
      });

      return () => {
        document.querySelectorAll(".reveal-on-scroll").forEach((el) => {
          observer.unobserve(el);
        });
      };
    }, 50);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location, hasAccess]);

  // Show email gate if not verified
  if (!hasAccess) {
    return <EmailGate onAccessGranted={handleAccessGranted} />;
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden bg-white dark:bg-gray-900 transition-colors duration-300">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="container mx-auto text-center relative z-10 reveal-on-scroll">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              {t('ems_hero_title')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              A modern energiarendszerek alapja a megbízható vezérlés és a valós idejű adat, ezt biztosítják intelligens EMS megoldásaink, amelyek garantálják a stabil működést, a költséghatékonyságot és a maximális termelési teljesítményt. Ennek köszönhetően napelemes és energiatárolós beruházásai nemcsak biztonságosan üzemelnek, hanem gyorsan meg is térülnek.
            </p>
          </div>
        </section>

        {/* Calculator Section */}
        <section id="calculator" className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="container mx-auto reveal-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              {t('ems_calculator_title')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto text-center">
              {t('ems_calculator_subtitle')}
            </p>
            <div className="max-w-2xl mx-auto space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">{t('ems_calculator_point1')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">hogy könnyen áttekinthessék, milyen modulokkal egészíthető ki a rendszer,</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">{t('ems_calculator_point3')}</p>
              </div>
            </div>
            <div className="text-center">
              <Button 
                size="lg" 
                onClick={() => {
                  const formSection = document.getElementById('ems-form');
                  if (formSection) {
                    formSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="text-lg px-8 py-6"
              >
                {t('ems_cta_button')}
              </Button>
            </div>
          </div>
        </section>

        {/* Hardware & Software Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/5">
          <div className="container mx-auto reveal-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              {t('ems_hardware_title')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto text-center">
              {t('ems_hardware_subtitle')}
            </p>
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">{t('ems_hardware_point1')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">{t('ems_hardware_point2')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">{t('ems_hardware_point3')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">{t('ems_hardware_point4')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="container mx-auto reveal-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              {t('ems_why_title')}
            </h2>
            <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-primary mb-4" />
                <p className="text-foreground">10+ év tapasztalat energiarendszerek tervezése terén</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-primary mb-4" />
                <p className="text-foreground">{t('ems_why_point2')}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-primary mb-4" />
                <p className="text-foreground">{t('ems_why_point3')}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-primary mb-4" />
                <p className="text-foreground">{t('ems_why_point4')}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow md:col-span-2">
                <CheckCircle className="w-8 h-8 text-primary mb-4" />
                <p className="text-foreground">{t('ems_why_point5')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Includes Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary/5">
          <div className="container mx-auto reveal-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              {t('ems_pricing_title')}
            </h2>
            <div className="max-w-2xl mx-auto grid sm:grid-cols-2 gap-4">
              <div className="bg-background border-2 border-primary/20 rounded-lg p-6 text-center">
                <p className="font-semibold text-lg">{t('ems_pricing_hardware')}</p>
              </div>
              <div className="bg-background border-2 border-primary/20 rounded-lg p-6 text-center">
                <p className="font-semibold text-lg">Szoftver - 1 vagy 5 évre</p>
              </div>
              <div className="bg-background border-2 border-primary/20 rounded-lg p-6 text-center">
                <p className="font-semibold text-lg">{t('ems_pricing_config')}</p>
              </div>
              <div className="bg-background border-2 border-primary/20 rounded-lg p-6 text-center">
                <p className="font-semibold text-lg">{t('ems_pricing_installation')}</p>
              </div>
            </div>
            <p className="text-muted-foreground text-center mt-8 max-w-2xl mx-auto">
              A fenti árak egyszeri díjak. Ezen felül mindössze 15 €/hónap/helyszín monitoring költség, valamint az Arbitrinity szolgáltatás díja merül fel, utóbbi egyedi megállapodás alapján.
            </p>
          </div>
        </section>

        {/* Calculator Form */}
        <EMSCalculatorForm />

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 to-secondary/10">
          <div className="container mx-auto text-center reveal-on-scroll">
            <p className="text-xl text-foreground mb-6 max-w-3xl mx-auto">
              {t('ems_cta_text')}
            </p>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
              {t('ems_cta_contact')}
            </p>
            <Button 
              size="lg" 
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/#contact';
                }
              }}
              className="text-lg px-8 py-6"
            >
              {t('contact')}
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EMSCalculator;